//const API_URL = 'http://localhost:5000/api';
const API_URL = `${process.env.REACT_APP_API_URL}/api`;

// pomocnicza funkcja do autoryzowanych requestów (JSON)
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: 'Błąd sieci lub serwera' };
    }
    const error = new Error(errorData.error || `Błąd ${response.status}`);
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

// pomocnicza funkcja do requestów z FormData (projekty, resume z plikami)
const authFormFetch = async (url, method, formData) => {
  const token = localStorage.getItem('adminToken');
  const response = await fetch(`${API_URL}${url}`, {
    method,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Błąd ${response.status}`);
  }

  return response.json();
};

// simple cache dla getów (Map)
const cache = new Map();

// funk pomocnicza do cach odpowiedzi GET
const cachedGet = async (cacheKey, fetchFn) => {
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const data = await fetchFn();
  cache.set(cacheKey, data);
  return data;
};

// Auth
export const login = async (credentials) => {
  const data = await authFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  if (data.token) {
    localStorage.setItem('adminToken', data.token);
  }
  return data;
};

export const logout = () => {
  localStorage.removeItem('adminToken');
  cache.clear(); // clear cache po wylogowaniu
  window.location.href = '/';
};

// About Me

export const getAboutSections = async () => {
  const cacheKey = 'about_sections';
  return cachedGet(cacheKey, () => authFetch('/about'));
};

export const getAboutSection = (id) => authFetch(`/about/${id}`);
export const createAboutSection = (data) =>
  authFetch('/about', { method: 'POST', body: JSON.stringify(data) });
export const updateAboutSection = (id, data) =>
  authFetch(`/about/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAboutSection = (id) =>
  authFetch(`/about/${id}`, { method: 'DELETE' });

// Projects

export const getProjects = async () => {
  const cacheKey = 'projects';
  return cachedGet(cacheKey, () => authFetch('/projects'));
};

export const getProject = (id) => authFetch(`/projects/${id}`);
export const createProject = (formData) =>
  authFormFetch('/projects', 'POST', formData);
export const updateProject = (id, formData) =>
  authFormFetch(`/projects/${id}`, 'PUT', formData);
export const deleteProject = (id) =>
  authFetch(`/projects/${id}`, { method: 'DELETE' });

// Resume

export const getResumeEntries = async (category = '') => {
  const cacheKey = `resume_${category || 'all'}`;
  return cachedGet(cacheKey, () =>
    authFetch(category ? `/resume?category=${category}` : '/resume')
  );
};

export const getResumeEntry = (id) => authFetch(`/resume/${id}`);

export const createResumeEntry = (formData) =>
  authFormFetch('/resume', 'POST', formData);

export const updateResumeEntry = (id, formData) =>
  authFormFetch(`/resume/${id}`, 'PUT', formData);

export const deleteResumeEntry = (id) =>
  authFetch(`/resume/${id}`, { method: 'DELETE' });

// Aside (sidebar / user info)

export const getAsideEntries = async (type = '') => {
  const cacheKey = `aside_${type || 'all'}`;
  return cachedGet(cacheKey, () =>
    authFetch(type ? `/aside?type=${type}` : '/aside')
  );
};

export const getAsideEntry = async (id) => {
  const cacheKey = `aside_entry_${id}`;
  return cachedGet(cacheKey, () => authFetch(`/aside/entry/${id}`));
};

export const createAsideEntry = (formData) =>
  authFormFetch('/aside', 'POST', formData);

export const updateAsideEntry = (id, formData) =>
  authFormFetch(`/aside/${id}`, 'PUT', formData);

export const deleteAsideEntry = (id) =>
  authFetch(`/aside/${id}`, { method: 'DELETE' });

// Blog
export const getBlogPosts = async () => {
  const cacheKey = 'blog_posts';
  return cachedGet(cacheKey, () => authFetch('/blog'));
};

export const getBlogPost = async (identifier) => {
  // identifier może być slug lub id (string)
  const cacheKey = `blog_post_${identifier}`;
  return cachedGet(cacheKey, () => authFetch(`/blog/${identifier}`));
};

export const createBlogPost = (formData) =>
  authFormFetch('/blog', 'POST', formData);

export const updateBlogPost = (id, formData) =>
  authFormFetch(`/blog/${id}`, 'PUT', formData);

export const deleteBlogPost = (id) =>
  authFetch(`/blog/${id}`, { method: 'DELETE' });

export const incrementBlogPostView = (id) =>
  authFetch(`/blog/${id}/increment-view`, { method: 'PATCH' });

//contact

export const sendContactMessage = async (data) => {
  const response = await fetch(`${API_URL}/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    // Rzucamy cały obiekt, aby frontend mógł sprawdzić fieldErrors
    throw result;
  }

  return result;
};

// Eksport obiektu api
const api = {
  login,
  logout,
  getAboutSections,
  getAboutSection,
  createAboutSection,
  updateAboutSection,
  deleteAboutSection,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getResumeEntries,
  getResumeEntry,
  createResumeEntry,
  updateResumeEntry,
  deleteResumeEntry,
  getAsideEntries,
  getAsideEntry,
  createAsideEntry,
  updateAsideEntry,
  deleteAsideEntry,
  //blog
  getBlogPosts,
  getBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  incrementBlogPostView,
  //contact
  sendContactMessage,
};

export default api;
